import { ConfigProvider } from "antd";

export type ThemeConfig = Record<string, Record<string, string | undefined>>;

export type ThemeConfigInputType =
  | "checkboxInput"
  | "hexColorInput"
  | "selectInput";

export interface ThemeConfigFieldOption {
  label: string;
  value: string;
}

export interface ThemeConfigFieldSchema {
  label: string;
  type: ThemeConfigInputType;
  default?: string;
  options?: ThemeConfigFieldOption[];
}

export interface ThemeConfigSectionSchema {
  label: string;
  fields: Record<string, ThemeConfigFieldSchema>;
}

// Keep in sync with FONT_FAMILIES + @font-face in homeBuyer.
const FONT_OPTIONS: ThemeConfigFieldOption[] = [
  { label: "Inter", value: "Inter UI" },
  { label: "Euclid Circular B", value: "EuclidCircularB" },
  { label: "Gotham Narrow", value: "Gotham Narrow" },
  { label: "Domaine Display", value: "Domaine Display" },
];

export const THEME_CONFIG_TEMPLATE: Record<string, ThemeConfigSectionSchema> = {
  layout: {
    label: "Layout",
    fields: {
      menubar: {
        type: "selectInput",
        label: "Menubar Position",
        default: "left",
        options: [
          { label: "Left", value: "left" },
          { label: "Top", value: "top" },
          // { label: "Right", value: "right" },
        ],
      },
      visitHome: {
        type: "selectInput",
        label: "Visit Home Position",
        default: "left",
        options: [
          { label: "Left", value: "left" },
          { label: "Fullscreen", value: "fullscreen" },
          // { label: "Top", value: "top" },
          // { label: "Right", value: "right" },
          // { label: "Bottom", value: "bottom" },
        ],
      },
      nodeOption: {
        type: "selectInput",
        label: "Node Option Position",
        default: "left",
        options: [
          { label: "Left", value: "left" },
          { label: "Right", value: "right" },
          // { label: "Top", value: "top" },
          // { label: "Bottom", value: "bottom" },
        ],
      },
    },
  },
  colors: {
    label: "Colors",
    fields: {
      // NOTE: `theme`, not `menubarTheme`: only the menubar recolours today, the rest follows
      // on the antd v5 upgrade, and the stored value does not have to move.
      theme: {
        type: "selectInput",
        label: "Theme",
        default: "dark",
        options: [
          { label: "Light", value: "light" },
          { label: "Dark", value: "dark" },
        ],
      },
      brandPrimary: {
        type: "hexColorInput",
        label: "Primary",
        default: "--brand-primary",
      },
      brandMedium: {
        type: "hexColorInput",
        label: "Medium",
        default: "--brand-medium",
      },
      brandAccent: {
        type: "hexColorInput",
        label: "Accent",
        default: "--brand-accent",
      },
    },
  },
  typography: {
    label: "Typography",
    fields: {
      headingFont: {
        type: "selectInput",
        label: "Heading Font",
        default: "Inter UI",
        options: FONT_OPTIONS,
      },
      bodyFont: {
        type: "selectInput",
        label: "Body Font",
        default: "Inter UI",
        options: FONT_OPTIONS,
      },
    },
  },
  features: {
    label: "Features",
    fields: {
      profile: {
        type: "checkboxInput",
        label: "Profile",
        default: "false",
      },
    },
  },
};

export class ThemeConfigure {
  // Read authored :root from stylesheets — getComputedStyle returns apply()'s inline override, not the default.
  static resolve(token: string): string {
    if (!token.startsWith("--")) {
      return token;
    }
    for (const sheet of document.styleSheets) {
      let rules: CSSRuleList;
      try {
        rules = sheet.cssRules;
      } catch {
        continue;
      }
      for (const rule of rules) {
        if (rule instanceof CSSStyleRule && rule.selectorText === ":root") {
          const value = rule.style.getPropertyValue(token).trim();
          if (value) {
            return value;
          }
        }
      }
    }
    return "";
  }

  static withDefaults(themeConfig?: ThemeConfig): ThemeConfig {
    const result: ThemeConfig = { ...themeConfig };
    Object.entries(THEME_CONFIG_TEMPLATE).forEach(([section, schema]) => {
      const current = { ...result[section] };
      Object.entries(schema.fields).forEach(([key, field]) => {
        if (!current[key] && field.default) {
          current[key] = this.resolve(field.default);
        }
      });
      result[section] = current;
    });
    return result;
  }

  static apply(themeConfig?: ThemeConfig): void {
    const { colors = {} } = themeConfig || {};

    const root = document.documentElement;

    // NOTE: accent is the button and link colour in the design, which is what antd calls
    // primary; the navy Brand/Primary is a surface colour, not an action colour.
    if (colors?.brandAccent) {
      ConfigProvider.config({
        theme: {
          primaryColor: this.resolve(colors.brandAccent),
        },
      });
    }

    // NOTE: antd has no token for the other two brand colours — expose all three as
    // CSS vars, which the semantic vars in _global.less are defined in terms of.
    // https://github.com/ant-design/ant-design/blob/4.x-stable/components/style/themes/default.less
    const cssColors = {
      "--brand-primary": colors.brandPrimary,
      "--brand-medium": colors.brandMedium,
      "--brand-accent": colors.brandAccent,
    };
    Object.entries(cssColors).forEach(([cssVar, value]) => {
      if (value) {
        root.style.setProperty(cssVar, this.resolve(value));
      }
    });

    // TODO: upgrade to antd v5 — ConfigProvider theme.token themes
    // font/fontSize/border at runtime. antd v4 bakes these into LESS, so
    // non-color tokens must go through our own CSS vars (root.style.setProperty).
  }
}
